import type React from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Typography,
} from "@mui/material";
import { Add, EmojiEvents, Home, Church, Layers } from "@mui/icons-material";
import { motion, type Variants } from "framer-motion";
import PackageCard from "./PackageCard";
import type { IPackage } from "../../../../types/package";
import { useThemeColors } from "../hooks/UseThemeColors";
import PaginationControls from "./PaginationControl";

interface PackagesSectionProps {
  activeTab: number;
  onTabChange: (index: number) => void;
  packages: IPackage[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onAddPackage?: () => void;
  onCreateCombo?: () => void;
  onEditPackage: (pkg: IPackage) => void;
  currentPage: number;
  totalCount: number;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

const tabs = [
  "Funeral Packages",
  "Funeral Homes",
  "Church Services",
  "Conference",
];

const PackagesSection: React.FC<PackagesSectionProps> = ({
  activeTab,
  onTabChange,
  packages,
  isLoading,
  error,
  onRetry,
  onAddPackage,
  onCreateCombo,
  onEditPackage,
  currentPage,
  totalCount,
  onNextPage,
  onPreviousPage,
}) => {
  const {
    primaryColor,
    primaryHover,
    primaryTextColor,
    secondaryHighlight,
    whiteColor,
    neutralBackground,
  } = useThemeColors();

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const totalPages = Math.ceil(totalCount / 10);

  const getPackageIcons = (index: number) => {
    const icons = [
      <EmojiEvents key="0" />,
      <Home key="1" />,
      <Church key="2" />,
      <Church key="3" />,
    ];
    return icons[index];
  };

  const packagesWithIcons = packages.map((pkg) => ({
    ...pkg,
    icon: getPackageIcons(activeTab),
  }));

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => onTabChange(newValue)}
          sx={{
            mb: 2,
            "& .MuiTabs-indicator": { backgroundColor: primaryColor },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "medium",
              fontSize: "1rem",
              color: primaryTextColor,
              "&.Mui-selected": { color: primaryColor },
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab} />
          ))}
        </Tabs>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onCreateCombo && (
            <Button
              variant="outlined"
              startIcon={<Layers />}
              onClick={onCreateCombo}
              sx={{
                borderColor: primaryColor,
                color: primaryColor,
                textTransform: "none",
                "&:hover": {
                  borderColor: primaryHover,
                  backgroundColor: `${primaryColor}10`,
                },
              }}
            >
              ADD COMBO PACKAGE
            </Button>
          )}
          {onAddPackage && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onAddPackage}
              sx={{
                backgroundColor: primaryColor,
                color: whiteColor,
                textTransform: "none",
                "&:hover": { backgroundColor: primaryHover },
              }}
            >
              ADD PACKAGE
            </Button>
          )}
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress sx={{ color: primaryColor }} />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, color: primaryTextColor, bgcolor: neutralBackground }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : packages.length > 0 ? (
        <>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={2}>
              {packagesWithIcons.map((pkg) => (
                <Grid size={{ xs: 12, md: 4 }} key={pkg.id}>
                  <PackageCard pkg={pkg} onEdit={onEditPackage} />
                </Grid>
              ))}
            </Grid>
          </motion.div>
          {totalCount > 10 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPrevious={onPreviousPage}
              onNext={onNextPage}
            />
          )}
        </>
      ) : (
        <Typography
          sx={{ textAlign: "center", py: 5, color: secondaryHighlight }}
        >
          No packages available for this category.
        </Typography>
      )}
    </Box>
  );
};

export default PackagesSection;
